import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box, Typography, Avatar, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemAvatar, ListItemText, ListItemButton, Divider, InputBase } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import useApp from "../../../store/contexts/AppContext";
import useCustomSnackbar from "../../../store/contexts/SnackbarContext";
import {
  MESSAGE_FILE_TYPES,
  MESSAGE_INTERACTIONS_TYPES,
  CHAT_ROOMS,
  SNACKBAR_VARIANT,
} from "../../../config/index";
import SOCKET_EVENTS from "../../../config/SocketEvents";
import ACTIONS from "../../../store/actions";
import TruncateString from "../../../utils/TruncateString";
import FormatDateDurationForMessagesTime from "../../../utils/FormatDateDurationForMessagesTime";
import FormatDateDurationForMessagesDate from "../../../utils/FormatDateDurationForMessagesDate";
import capitalizeEachFirstWord from "../../../utils/CapLetters";
import FormatNumberCounter from "../../../utils/FormatNumberCounter";
import { getParticipantData, getTeamCode } from "../../../utils/RoomHelpers";
import { getMessagePosition } from "../../../utils/messageHelpers";
import parseMessageText from "../../../utils/parseMessageText";
import { loadGlobalPrefs } from "../../../pages/Settings/index";
import { TYPING_EMIT_DELAY } from "../../../constants/messageConstants";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";
import EmojiPicker from "./EmojiPicker";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import SwipeableMessage from "./SwipeableMessage";
import ChatSearch from "./ChatSearch";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import EditIcon from "@mui/icons-material/Edit";
import ReplyIcon from "@mui/icons-material/Reply";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CancelIcon from "@mui/icons-material/Cancel";
import ImageIcon from "@mui/icons-material/Image";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PushPinIcon from "@mui/icons-material/PushPin";
import ReplayIcon from "@mui/icons-material/Replay";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import notificationSound from "../../../utils/notificationSound";

// Styled Components
const MessageFieldContainer = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  background:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.paper,
}));

const BalanceContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "10px 20px",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.dark, 0.03)})`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.dark, 0.02)})`,
  backdropFilter: "blur(10px)",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.05) : alpha("#000", 0.06)}`,
  color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
  fontSize: "14px",
  fontWeight: 500,
  flexShrink: 0,
  minHeight: "44px",
  alignItems: "center",
}));

const BalanceItem = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 12px",
  borderRadius: "8px",
  background:
    theme.palette.mode === "dark" ? alpha("#000", 0.2) : alpha("#000", 0.04),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,

  "& b": {
    color:
      theme.palette.mode === "dark"
        ? alpha("#fff", 0.7)
        : alpha(theme.palette.text.primary, 0.7),
    fontWeight: 600,
  },
}));

const BalanceValue = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "15px",
}));

const MessagesContainer = styled("div")(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "20px",
  minHeight: 0,
  position: "relative",
  background:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.paper,

  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "rgba(128,128,128,0.08)",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(128,128,128,0.35)",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(128,128,128,0.55)",
  },
}));

const MessagesTopNotice = styled("div")(({ theme }) => ({
  textAlign: "center",
  color:
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.5)
      : alpha(theme.palette.text.secondary, 0.6),
  fontSize: "13px",
  padding: "12px",
  margin: "0 auto 20px",
  background: alpha(theme.palette.primary.main, 0.05),
  borderRadius: "8px",
  maxWidth: "300px",
}));

const LoadingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "8px 18px",
  marginBottom: "10px",
  alignSelf: "center",
  width: "fit-content",
  margin: "0 auto 10px",
  background:
    theme.palette.mode === "dark"
      ? "rgba(28,28,34,0.88)"
      : "rgba(255,255,255,0.88)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  borderRadius: "20px",
  boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
  "& .spinner": {
    width: "16px",
    height: "16px",
    border: `2.5px solid ${alpha(theme.palette.primary.main, 0.25)}`,
    borderTop: `2.5px solid ${theme.palette.primary.main}`,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
    flexShrink: 0,
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

const EmptyState = styled("div")(({ theme }) => ({
  textAlign: "center",
  color:
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.5)
      : alpha(theme.palette.text.secondary, 0.6),
  padding: "60px 20px",

  "& h2": {
    fontSize: "20px",
    fontWeight: 600,
    margin: 0,
  },
}));

const DateDemarcation = styled("div")(({ theme }) => ({
  textAlign: "center",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.85)"
      : "rgba(0,0,0,0.65)",
  fontSize: "12px",
  fontWeight: 600,
  margin: "20px auto",
  padding: "6px 16px",
  background:
    theme.palette.mode === "dark"
      ? "rgba(30,30,35,0.88)"
      : "rgba(255,255,255,0.88)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: "20px",
  width: "fit-content",
  display: "flex",
  justifyContent: "center",
  boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
}));

const MessageItem = styled("div")(({ isReplied, isHighlighted, theme }) => ({
  marginBottom: "16px",
  animation: isReplied
    ? "highlight 2s ease-in-out"
    : isHighlighted
      ? "searchHighlight 1.5s ease-in-out"
      : "none",
  position: "relative",
  borderRadius: "12px",
  padding: isHighlighted ? "4px" : 0,

  "@keyframes highlight": {
    "0%, 100%": {
      boxShadow: "none",
      backgroundColor: "transparent",
    },
    "15%, 85%": {
      // Glass-like ring that adapts to any message background or wallpaper:
      // inner white ring stays visible on dark, outer primary ring on light.
      boxShadow: [
        `0 0 0 2px rgba(255,255,255,0.55)`,
        `0 0 0 4px ${alpha(theme.palette.primary.main, 0.35)}`,
        `0 6px 20px rgba(0,0,0,0.18)`,
      ].join(", "),
      backgroundColor: alpha(theme.palette.primary.main, 0.09),
      backdropFilter: "blur(4px) saturate(160%)",
      borderRadius: "12px",
    },
  },
  "@keyframes searchHighlight": {
    "0%, 100%": {
      boxShadow: `0 0 0 2px transparent`,
    },
    "50%": {
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
    },
  },
}));

const MessageRow = styled("div")(({ isSelf }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: "8px",
  flexDirection: isSelf ? "row-reverse" : "row",
  width: "100%",
}));

const SenderAvatar = styled(Avatar)(({ theme }) => ({
  width: 28,
  height: 28,
  fontSize: "11px",
  fontWeight: 700,
  flexShrink: 0,
  background: theme.palette.mode === "dark" ? "#2a2a2a" : "#ffffff",
  color: theme.palette.mode === "dark" ? "#bbb" : "#555",
  border: `1.5px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.1)}`,
  marginBottom: "2px",
}));

const MessageBody = styled("div")(({ isSelf, theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "10px 14px 8px",
  background: isSelf
    ? theme.palette.mode === "dark"
      ? "rgba(55, 60, 78, 0.93)"
      : "rgba(255, 255, 255, 0.90)"
    : theme.palette.mode === "dark"
      ? "rgba(26, 26, 32, 0.93)"
      : "rgba(255, 255, 255, 0.90)",
  backdropFilter: "blur(24px) saturate(160%)",
  WebkitBackdropFilter: "blur(24px) saturate(160%)",
  borderRadius: "14px",
  border: `1px solid ${
    isSelf
      ? theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,0,0,0.09)"
      : theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.08)"
  }`,
  boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
  maxWidth: "85%",
  marginLeft: isSelf ? "auto" : "0",
  position: "relative",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.95)"
      : "rgba(0,0,0,0.88)",
}));

const DeletedBubble = styled("div")(({ isSelf, theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 14px",
  borderRadius: "12px",
  maxWidth: "85%",
  marginLeft: isSelf ? "auto" : "0",
  background:
    theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#000", 0.04),
  border: `1px dashed ${theme.palette.mode === "dark" ? alpha("#fff", 0.15) : alpha("#000", 0.12)}`,
  color:
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.4)
      : alpha(theme.palette.text.secondary, 0.7),
  fontSize: "13px",
  fontStyle: "italic",
  userSelect: "none",
}));

const MessageHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "8px",
}));

const UsernameBadge = styled("div")(({ teamCode, theme }) => ({
  padding: "4px 10px",
  borderRadius: "6px",
  fontSize: "13px",
  fontWeight: 700,
  background:
    teamCode === "admin"
      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
      : alpha(theme.palette.primary.main, 0.2),
  border: `1px solid ${
    teamCode === "admin"
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.3)
  }`,
  color:
    teamCode === "admin"
      ? "#fff"
      : theme.palette.mode === "dark"
        ? "#fff"
        : "#1a1a1a",
  display: "inline-block",
}));

const InteractionButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
}));

const InteractionButton = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "2px",
  fontSize: "12px",
  color:
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.7)
      : alpha(theme.palette.text.secondary, 0.7),

  "& .MuiIconButton-root": {
    padding: "4px",
  },
}));

const TimeAndActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "11px",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.50)"
      : "rgba(0,0,0,0.42)",
  "& .MuiIconButton-root": { padding: "3px" },
  "& .MuiSvgIcon-root": { fontSize: "15px !important" },
}));

const SenderName = styled("div")(({ teamCode, theme }) => ({
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.02em",
  marginBottom: "5px",
  color:
    teamCode === "admin"
      ? theme.palette.primary.main
      : theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.60)"
        : "rgba(0,0,0,0.48)",
}));

const MessageFooter = styled("div")({
  display: "flex",
  alignItems: "center",
  marginTop: "6px",
  gap: "4px",
});

const TipButton = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1px",
  fontSize: "11px",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.50)"
      : "rgba(0,0,0,0.42)",
  marginRight: "auto",
  "& .MuiIconButton-root": { padding: "2px" },
  "& .MuiSvgIcon-root": { fontSize: "13px !important" },
}));

const TickIcon = styled("span")(({ theme, isRead, isOnline }) => ({
  display: "inline-flex",
  alignItems: "center",
  color: isRead
    ? "#53BDEB" // Blue double tick - read
    : isOnline
      ? theme.palette.mode === "dark"
        ? alpha("#fff", 0.55)
        : alpha("#000", 0.4) // Gray double tick - online but not read
      : theme.palette.mode === "dark"
        ? alpha("#fff", 0.55)
        : alpha("#000", 0.4), // Gray single tick - offline
  lineHeight: 1,
  "& .MuiSvgIcon-root": {
    fontSize: "14px",
  },
}));

const ReplyPreview = styled("div")(({ theme }) => ({
  padding: "8px 12px",
  background:
    theme.palette.mode === "dark" ? "rgba(0,0,0,0.28)" : "rgba(0,0,0,0.07)",
  borderLeft: `3px solid ${theme.palette.primary.main}`,
  borderRadius: "6px",
  fontSize: "13px",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.80)"
      : "rgba(0,0,0,0.65)",
  cursor: "pointer",
  transition: "all 0.2s",

  "&:hover": {
    background:
      theme.palette.mode === "dark" ? "rgba(0,0,0,0.38)" : "rgba(0,0,0,0.11)",
    color: theme.palette.mode === "dark" ? "#fff" : "rgba(0,0,0,0.85)",
  },

  "& span": {
    fontStyle: "italic",
  },
}));

const MessageText = styled("div")(({ theme }) => ({
  fontSize: "inherit",
  lineHeight: 1.5,
  wordBreak: "break-word",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.95)"
      : "rgba(0,0,0,0.88)",
  userSelect: "text",
  cursor: "text",
}));

const SystemNotice = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  margin: "8px 16px",
  "& span": {
    padding: "5px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    lineHeight: 1.5,
    color:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.92)"
        : "rgba(0,0,0,0.82)",
    background:
      theme.palette.mode === "dark"
        ? "rgba(20,20,28,0.88)"
        : "rgba(255,255,255,0.92)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
    textAlign: "center",
    userSelect: "text",
    maxWidth: "75%",
  },
}));

function formatBytes(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ImageMessageBubble({
  src,
  caption,
  isRecent = false,
  forceReveal = false,
  onLoaded,
}) {
  const globalPrefs = loadGlobalPrefs();
  const autoDownload = globalPrefs.autoDownloadMedia !== false;
  // Force-reveal for locally-previewed (blob) images — the user just sent this, always show it.
  // Also auto-reveal when global auto-download is on, or this is one of the last 5 images.
  const [revealed, setRevealed] = React.useState(
    forceReveal || autoDownload || isRecent,
  );
  const [fileSize, setFileSize] = React.useState(null);

  // Lazily fetch file size via HEAD request when showing the placeholder
  React.useEffect(() => {
    if (revealed || !src) return;
    let cancelled = false;
    fetch(src, { method: "HEAD" })
      .then((res) => {
        if (!cancelled) {
          const len = res.headers.get("content-length");
          if (len) setFileSize(Number(len));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [revealed, src]);

  if (revealed) {
    return (
      <div>
        <img
          src={src}
          alt="sent"
          style={{
            maxWidth: "100%",
            maxHeight: "280px",
            borderRadius: "10px",
            display: "block",
            objectFit: "cover",
            cursor: "pointer",
          }}
          onClick={() => window.open(src, "_blank")}
          onLoad={onLoaded}
        />
        {caption && (
          <MessageText style={{ marginTop: "6px" }}>
            {parseMessageText(caption)}
          </MessageText>
        )}
      </div>
    );
  }
  return (
    <div
      onClick={() => setRevealed(true)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        padding: "10px 14px",
        borderRadius: "12px",
        border: "1px solid rgba(128,128,128,0.25)",
        background: "rgba(128,128,128,0.07)",
        color: "inherit",
        fontSize: "13px",
        userSelect: "none",
      }}
    >
      <ImageIcon sx={{ fontSize: 22, opacity: 0.7 }} />
      <span>
        <span style={{ display: "block", fontWeight: 500 }}>
          Tap to load image
        </span>
        {fileSize && (
          <span style={{ display: "block", fontSize: "11px", opacity: 0.6 }}>
            {formatBytes(fileSize)}
          </span>
        )}
      </span>
    </div>
  );
}

const ReplyContainer = styled("div")(({ theme }) => ({
  padding: "12px 20px",
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.08),
  borderTop: `1px solid ${
    theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08)
  }`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexShrink: 0,
  minHeight: "52px",
}));

const InputContainer = styled("div")(({ theme }) => ({
  padding: "16px 20px",
  background:
    theme.palette.mode === "dark" ? alpha("#0a0a0a", 0.95) : "#fafafa",
  borderTop: `1px solid ${
    theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.12)
  }`,
  display: "flex",
  alignItems: "flex-end",
  gap: "12px",
  flexShrink: 0,
  minHeight: "80px",
  backdropFilter: "blur(10px)",
  position: "relative",

  "& textarea": {
    flex: 1,
    minHeight: "44px",
    maxHeight: "120px",
    padding: "12px 16px",
    background:
      theme.palette.mode === "dark" ? alpha("#1a1a1a", 0.8) : "#f5f5f5",
    border: `1px solid ${
      theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.15)
    }`,
    borderRadius: "12px",
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
    outline: "none",
    transition: "all 0.3s",

    "&::placeholder": {
      color:
        theme.palette.mode === "dark"
          ? alpha("#fff", 0.4)
          : alpha(theme.palette.text.secondary, 0.4),
    },

    "&:focus": {
      border: `1px solid ${theme.palette.primary.main}`,
      background:
        theme.palette.mode === "dark" ? alpha("#1a1a1a", 0.95) : "#efefef",
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
  },
}));

const ButtonGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "6px",
  flexShrink: 0,
});

const IconRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "2px",
});

const SendButton = styled("button")(({ theme, disabled }) => ({
  padding: "10px 24px",
  background: disabled
    ? alpha("#666", 0.3)
    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  border: "none",
  borderRadius: "10px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.3s",
  opacity: disabled ? 0.5 : 1,
  minWidth: "90px",

  "&:hover": {
    transform: disabled ? "none" : "translateY(-2px)",
    boxShadow: disabled
      ? "none"
      : `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
  },

  "&:active": {
    transform: disabled ? "none" : "translateY(0)",
  },
}));

const CancelButton = styled("button")(({ theme }) => ({
  padding: "6px 16px",
  background: "transparent",
  border: `1px solid ${
    theme.palette.mode === "dark" ? alpha("#fff", 0.2) : alpha("#000", 0.15)
  }`,
  borderRadius: "8px",
  color:
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.7)
      : alpha(theme.palette.text.secondary, 0.7),
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s",

  "&:hover": {
    background:
      theme.palette.mode === "dark" ? alpha("#fff", 0.05) : alpha("#000", 0.05),
    color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
    borderColor:
      theme.palette.mode === "dark" ? alpha("#fff", 0.3) : alpha("#000", 0.2),
  },
}));

const Countdown = styled("div")(({ theme }) => ({
  fontSize: "12px",
  color:
    theme.palette.mode === "dark"
      ? alpha("#fff", 0.5)
      : alpha(theme.palette.text.secondary, 0.5),
  fontWeight: 600,
  padding: "4px 8px",
  background: alpha(theme.palette.primary.main, 0.1),
  borderRadius: "6px",
}));

const ScrollToBottomButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: "100px",
  right: "20px",
  width: "36px",
  height: "36px",
  background: "rgba(255,255,255,0.18)",
  backdropFilter: "blur(16px) saturate(180%)",
  WebkitBackdropFilter: "blur(16px) saturate(180%)",
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.90)"
      : "rgba(0,0,0,0.75)",
  borderRadius: "50%",
  border: "1px solid rgba(255,255,255,0.22)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
  transition: "all 0.22s ease",
  zIndex: 10,

  "&:hover": {
    background: "rgba(255,255,255,0.28)",
    transform: "scale(1.08)",
    boxShadow: "0 4px 18px rgba(0,0,0,0.22)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
}));

const NewMessageBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "-8px",
  right: "-8px",
  minWidth: "20px",
  height: "20px",
  background: "#ff4444",
  color: "#fff",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 700,
  padding: "2px 6px",
  border: "2px solid",
  borderColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
}));

const MessageSearchContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "8px",
  left: "20px",
  right: "20px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  background:
    theme.palette.mode === "dark"
      ? alpha("#1a1a1a", 0.95)
      : alpha("#ffffff", 0.98),
  borderRadius: "12px",
  border: `1px solid ${
    theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.1)
  }`,
  boxShadow: `0 4px 16px ${alpha("#000", 0.2)}`,
  zIndex: 10,
}));

const MessageSearchInput = styled("input")(({ theme }) => ({
  flex: 1,
  border: "none",
  background: "transparent",
  color: theme.palette.mode === "dark" ? "#fff" : "#1a1a1a",
  fontSize: "14px",
  outline: "none",
  padding: "4px 8px",
  "&::placeholder": {
    color:
      theme.palette.mode === "dark" ? alpha("#fff", 0.4) : alpha("#000", 0.4),
  },
}));

const SearchNavButton = styled(IconButton)(({ theme }) => ({
  padding: "4px",
  color:
    theme.palette.mode === "dark" ? alpha("#fff", 0.7) : alpha("#000", 0.6),
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
}));

const SearchCount = styled("span")(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  color:
    theme.palette.mode === "dark" ? alpha("#fff", 0.6) : alpha("#000", 0.6),
  minWidth: "50px",
  textAlign: "center",
}));

const HighlightedMessage = styled("div")(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.15),
  borderRadius: "8px",
  padding: "2px 4px",
  margin: "-2px -4px",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: alpha(theme.palette.primary.main, 0.8),
}));

const MessageField = ({
  otherUser = {},
  otherUserOnline = false,
  innerRoom,
  roomModerators = [],
  triggerSearch = 0,
}) => {
  const {
    appState: { activeChat, userData, socket, chats },
    sendMessageToChat,
    deleteChatMessageApiCall,
    deleteMessageForMeApiCall,
    editChatMessageApiCall,
    updateChatMessageInteractionApiCall,
    updateChatMessageTipApiCall,
    switchActiveChat,
    fetchMoreRoomMessagesApiCall,
    uploadMessageImageApiCall,
    typingByRoom,
    localSystemMessages,
    keepDisappearingMessageApiCall,
    unkeepDisappearingMessageApiCall,
    dispatch,
  } = useApp();

  const { showSnackbar } = useCustomSnackbar();

  const [dismissedMessages, setDismissedMessages] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("dismissedDeletedMessages") || "[]"),
      );
    } catch {
      return new Set();
    }
  });

  const dismissDeletedMessage = useCallback((messageId) => {
    setDismissedMessages((prev) => {
      const next = new Set(prev);
      next.add(messageId.toString());
      localStorage.setItem(
        "dismissedDeletedMessages",
        JSON.stringify([...next]),
      );
      return next;
    });
  }, []);

  const history = useHistory();

  const [message, setMessage] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [replyMessage, setReplyMessage] = useState(null);
  const [selectedRepliedMessageId, setSelectedRepliedMessageId] =
    useState(null);
  const [editedMessage, setEditedMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditableMessage, setIsEditableMessage] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [messageToForward, setMessageToForward] = useState(null);
  const [forwardSearch, setForwardSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  // Derive whether the other user is typing from the stable AppContext map
  const isOtherUserTyping =
    Object.keys(typingByRoom?.[activeChat?.id] || {}).length > 0;
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [messageSearchQuery, setMessageSearchQuery] = useState("");
  const [messageSearchResults, setMessageSearchResults] = useState([]);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const searchInputRef = useRef(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const selectedRepliedMessageIdRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const typingTimeoutRef = useRef(null);
  const messageInputRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const isLoadingRef = useRef(false);
  const isPaginationLoadRef = useRef(false); // true while loading older messages from DB
  const hasCompletedInitialScrollRef = useRef(false); // Permanent flag: true after first scroll
  const canLoadMoreMessagesRef = useRef(false); // Enable pagination only after user scrolls
  const initialScrollChatIdRef = useRef(null); // Track which chat we've scrolled for
  const isAtBottomRef = useRef(true); // Track if user is at bottom of messages
  const imageInputRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // pendingImage: { file, previewUrl } — set when user picks an image before sending
  const [pendingImage, setPendingImage] = useState(null);

  // Reliable scroll to the absolute bottom — more precise than scrollIntoView(block:'end')
  const scrollToExactBottom = useCallback((smooth = true) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (smooth) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Highlight search terms in message text (WhatsApp style - only text highlighted)
  const highlightSearchTerm = (text, query) => {
    if (!query || !text) return text;

    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <Box
          key={i}
          component="span"
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 235, 59, 0.5)"
                : "rgba(255, 193, 7, 0.7)",
            color: theme.palette.mode === "dark" ? "inherit" : "#000",
            fontWeight: 600,
            borderRadius: "3px",
            px: 0.3,
            py: 0.1,
          })}
        >
          {part}
        </Box>
      ) : (
        part
      ),
    );
  };

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURN
  // displayMessages derived synchronously so room_participants and messages are always
  // from the same activeChat object — prevents "Unknown" sender flash on chat switch.
  const displayMessages = useMemo(() => {
    if (!activeChat?.messages || !Array.isArray(activeChat.messages)) return [];
    if (activeChat.type === CHAT_ROOMS.TYPES.GROUP) {
      return activeChat.messages.slice(
        Math.max(activeChat.messages.length - 100, 0),
      );
    }
    return activeChat.messages;
  }, [activeChat?.messages, activeChat?.type, activeChat?.id]);

  const shouldShowOlderMessagesNotice = useMemo(() => {
    return (
      activeChat?.type === CHAT_ROOMS.TYPES.GROUP &&
      (activeChat?.messages?.length || 0) > 100
    );
  }, [activeChat?.messages?.length, activeChat?.type]);

  // On chat open, re-inject any blocked-period messages persisted in localStorage
  // Focus the message input whenever the active chat changes (covers search-result selection
  // which doesn't unmount/remount MessageField, so autoFocus alone doesn't re-fire)
  useEffect(() => {
    if (!activeChat?.id) return;
    // Clear draft text and reply state when switching chats so they don't bleed over
    setMessage("");
    setReplyMessage(null);
    setEditedMessage("");
    if (!pendingImage) {
      const t = setTimeout(() => messageInputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id]);

  useEffect(() => {
    if (!activeChat?.id) return;
    try {
      const stored = JSON.parse(
        localStorage.getItem("blocked_period_messages") || "{}",
      );
      const msgs = stored[activeChat.id];
      if (!msgs?.length) return;
      msgs.forEach((bpMsg) => {
        const alreadyPresent = activeChat.messages?.some(
          (m) => m.id === bpMsg.id,
        );
        if (!alreadyPresent) {
          dispatch({
            type: ACTIONS.ADD_BLOCKED_PERIOD_MESSAGE,
            payload: { roomId: activeChat.id, message: bpMsg },
          });
        }
      });
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (selectedRepliedMessageId) {
      selectedRepliedMessageIdRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const timeoutId = setTimeout(() => {
        setSelectedRepliedMessageId(null);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedRepliedMessageId]);

  useEffect(() => {
    if (activeChat?.messages && activeChat?.id) {
      const currentChatId = activeChat.id;
      const currentMessageCount = activeChat.messages.length;

      // If this is a new chat (different from what we last scrolled for), reset refs NOW
      // so the initial load check below sees previousMessageCount === 0 correctly.
      // The separate reset effect runs AFTER this one (same deps, later in code), so we
      // must reset here instead of relying on that effect's ordering.
      if (initialScrollChatIdRef.current !== currentChatId) {
        previousMessageCountRef.current = 0;
        canLoadMoreMessagesRef.current = false;
        isAtBottomRef.current = true;
      }

      const previousMessageCount = previousMessageCountRef.current;

      // Only scroll to bottom ONCE on initial load (when first opening chat)
      const hasScrolledForThisChat =
        initialScrollChatIdRef.current === currentChatId;
      const isInitialLoad =
        !hasScrolledForThisChat &&
        previousMessageCount === 0 &&
        currentMessageCount > 0 &&
        !canLoadMoreMessagesRef.current &&
        !isLoadingRef.current;

      if (isInitialLoad) {
        hasCompletedInitialScrollRef.current = true;
        initialScrollChatIdRef.current = currentChatId;
        isAtBottomRef.current = true;
        setNewMessageCount(0);

        setTimeout(() => {
          scrollToExactBottom(false); // instant on initial open, no animation

          setTimeout(() => {
            canLoadMoreMessagesRef.current = true;
          }, 800);
        }, 100);
      } else if (
        previousMessageCount > 0 &&
        currentMessageCount > previousMessageCount
      ) {
        if (isPaginationLoadRef.current) {
          // Older messages loaded from DB via pagination — don't count as new
        } else {
          // Real-time new message(s) arrived via socket
          const newMessagesAdded = currentMessageCount - previousMessageCount;

          if (isAtBottomRef.current) {
            setTimeout(() => scrollToExactBottom(true), 50);
          } else {
            setNewMessageCount((prev) => prev + newMessagesAdded);
            const soundPrefs = (() => {
              try {
                return (
                  JSON.parse(
                    localStorage.getItem(`chatPref_${activeChat?.id}`),
                  ) || {}
                );
              } catch {
                return {};
              }
            })();
            if (soundPrefs.sound !== false) notificationSound.play("message");
          }
        }
      }

      previousMessageCountRef.current = currentMessageCount;
    }
  }, [activeChat?.messages, activeChat?.id]);

  // Handle scroll to detect if user scrolled up and load more messages
  const handleMessagesScroll = useCallback(
    async (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isAtBottom = distanceFromBottom < 50;

      // Update bottom tracking
      isAtBottomRef.current = isAtBottom;

      // Clear new message count if scrolled to bottom
      if (isAtBottom && newMessageCount > 0) {
        setNewMessageCount(0);
      }

      // Emit read receipt when user reaches the bottom (respects read receipts pref)
      if (isAtBottom && activeChat?.id && activeChat?.type === "single") {
        const prefs = (() => {
          try {
            return (
              JSON.parse(localStorage.getItem(`chatPref_${activeChat.id}`)) ||
              {}
            );
          } catch {
            return {};
          }
        })();
        if (prefs.readReceipts !== false) {
          socket.emit(SOCKET_EVENTS.MARK_MESSAGES_READ, {
            roomId: activeChat.id,
          });
        }
      }

      setShowScrollButton(distanceFromBottom > 150);

      // Infinite scroll: Load more messages when scrolling near the top
      const distanceFromTop = scrollTop;
      const shouldLoadMore = distanceFromTop < 200; // Trigger when within 200px of top

      // Don't load if:
      // 1. Already loading
      // 2. No more pages to load
      // 3. Initial scroll hasn't completed yet (canLoadMoreMessagesRef is false)
      if (
        shouldLoadMore &&
        !isLoadingRef.current &&
        canLoadMoreMessagesRef.current &&
        activeChat?.pagination?.next_page
      ) {
        isLoadingRef.current = true;
        isPaginationLoadRef.current = true;
        setIsLoadingMoreMessages(true);

        // Save current scroll position
        previousScrollHeightRef.current = scrollHeight;

        try {
          const result = await fetchMoreRoomMessagesApiCall();

          if (result?.success) {
            // Restore scroll position after new messages are added
            setTimeout(() => {
              const container = messagesContainerRef.current;
              if (container) {
                const newScrollHeight = container.scrollHeight;
                const scrollDiff =
                  newScrollHeight - previousScrollHeightRef.current;
                container.scrollTop = scrollTop + scrollDiff;
              }
            }, 50);
          }
        } catch (error) {
          console.error("Error loading more messages:", error);
        } finally {
          setIsLoadingMoreMessages(false);
          isLoadingRef.current = false;
          // Reset after a tick so the scroll effect sees the flag while processing
          setTimeout(() => {
            isPaginationLoadRef.current = false;
          }, 300);
        }
      }
    },
    [
      activeChat?.pagination?.next_page,
      fetchMoreRoomMessagesApiCall,
      newMessageCount,
    ],
  );

  // Search through messages effect
  useEffect(() => {
    if (!messageSearchQuery.trim()) {
      setMessageSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    const query = messageSearchQuery.toLowerCase();
    const results = displayMessages
      .map((msg, index) => ({
        messageId: msg.id,
        index,
        matches: (
          msg.message?.toLowerCase().match(new RegExp(query, "g")) || []
        ).length,
      }))
      .filter((r) => r.matches > 0);

    setMessageSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 1 : 0);

    // Scroll to first result
    if (results.length > 0) {
      setTimeout(() => {
        const element = document.querySelector(
          `[data-message-id="${results[0].messageId}"]`,
        );
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [messageSearchQuery, displayMessages]);

  // Handle search result selection from ChatSearch
  const handleSearchResultSelect = useCallback((result, index) => {
    if (!result) return;

    setCurrentSearchIndex(index);

    // Scroll to the message using the actual message ID
    setTimeout(() => {
      const element = document.querySelector(
        `[data-message-id="${result.id}"]`,
      );
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 10);
  }, []);

  // Close button clears everything
  const handleCloseSearch = () => {
    setShowChatSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentSearchIndex(0);
  };

  // Navigate to next/prev search result
  const goToNextSearchResult = () => {
    if (messageSearchResults.length === 0) return;
    const newIndex =
      currentSearchIndex >= messageSearchResults.length
        ? 1
        : currentSearchIndex + 1;
    setCurrentSearchIndex(newIndex);
    const result = messageSearchResults[newIndex - 1];
    if (result) {
      const element = document.querySelector(
        `[data-message-id="${result.messageId}"]`,
      );
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const goToPrevSearchResult = () => {
    if (messageSearchResults.length === 0) return;
    const newIndex =
      currentSearchIndex <= 1
        ? messageSearchResults.length
        : currentSearchIndex - 1;
    setCurrentSearchIndex(newIndex);
    const result = messageSearchResults[newIndex - 1];
    if (result) {
      const element = document.querySelector(
        `[data-message-id="${result.messageId}"]`,
      );
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // When chat opens, mark messages as read (respects read receipts pref)
  useEffect(() => {
    if (activeChat?.id && activeChat?.type === "single") {
      const prefs = (() => {
        try {
          return (
            JSON.parse(localStorage.getItem(`chatPref_${activeChat.id}`)) || {}
          );
        } catch {
          return {};
        }
      })();
      if (prefs.readReceipts !== false) {
        socket.emit(SOCKET_EVENTS.MARK_MESSAGES_READ, {
          roomId: activeChat.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id]);

  // Stop emitting typing when switching chats
  useEffect(() => {
    if (isTyping) {
      setIsTyping(false);
      socket.emit(SOCKET_EVENTS.USER_TYPING, {
        roomId,
        receiverId: otherUser?.id,
        isTyping: false,
      });
    }
  }, [activeChat?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search trigger from header - toggle behavior
  useEffect(() => {
    if (triggerSearch > 0) {
      setShowChatSearch((prev) => !prev);
    }
  }, [triggerSearch]);

  // Clear UI search state and loading flags when switching chats.
  // NOTE: scroll-related refs (previousMessageCountRef, canLoadMoreMessagesRef,
  // initialScrollChatIdRef, isAtBottomRef) are reset inside the scroll effect above
  // so that they are always reset BEFORE the scroll logic evaluates them.
  useEffect(() => {
    setShowChatSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentSearchIndex(0);
    isLoadingRef.current = false;
    hasCompletedInitialScrollRef.current = false;
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  }, [activeChat?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global ESC handler - navigate to /messages when search is closed
  useEffect(() => {
    const handleGlobalEscape = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        // Only handle ESC when search is NOT open (ChatSearch handles its own ESC)
        if (!showChatSearch) {
          e.preventDefault();
          e.stopPropagation();
          switchActiveChat({});
          history.push("/messages");
        }
      }
    };

    window.addEventListener("keydown", handleGlobalEscape);
    return () => window.removeEventListener("keydown", handleGlobalEscape);
  }, [showChatSearch, history, switchActiveChat]);

  // SAFETY CHECK AFTER ALL HOOKS
  if (!activeChat || !userData) {
    return (
      <MessageFieldContainer>
        <EmptyState>
          <h2>Loading...</h2>
        </EmptyState>
      </MessageFieldContainer>
    );
  }

  const receiverId = otherUser?.id;
  const newChat = activeChat.has_connected_before;
  const roomId = activeChat.id;
  const roomTeams = activeChat.room_teams || [];

  const chatPrefs = (() => {
    try {
      return JSON.parse(localStorage.getItem(`chatPref_${roomId}`)) || {};
    } catch {
      return {};
    }
  })();
  const fontSizePx =
    { small: 13, normal: 15, large: 18 }[chatPrefs.fontSize || "normal"] || 15;
  const chatWallpaper = activeChat.chat_wallpaper || null;
  const wallpaperStyle = chatWallpaper
    ? chatWallpaper.startsWith("http") || chatWallpaper.startsWith("/")
      ? {
          backgroundImage: `url('${chatWallpaper}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : { background: chatWallpaper }
    : {};

  const isCurrentUserModerator =
    Array.isArray(roomModerators) &&
    roomModerators.some(
      (moderator) => moderator?.id?.toString() === userData?.id?.toString(),
    );

  const handleMenuOptionClick = (event, currentSelectedMessage) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedMessage(currentSelectedMessage);
    // Check ownership using the same comparison the rest of the component uses,
    // AND check the time window — store both as one flag so JSX has a single truth source.
    const isOwn = currentSelectedMessage?.sender_id?.toString() === userData?.id?.toString()
      || currentSelectedMessage?.sender === "self";
    setIsEditableMessage(isOwn && isMessageEditable(currentSelectedMessage));
    setAnchorEl(event.currentTarget);
  };

  const handleMenuOptionClose = () => {
    // Keep selectedMessage alive briefly so onClick handlers that fire
    // after onClose (MUI ordering) can still read it.
    setAnchorEl(null);
    setIsEditableMessage(false);
    setTimeout(() => setSelectedMessage(null), 100);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const previewUrl = URL.createObjectURL(file);
    setPendingImage({ file, previewUrl });
    // Focus textarea so user can type a caption
    setTimeout(() => messageInputRef.current?.focus(), 100);
  };

  const handleSendWithImage = async (
    captionText,
    retryFile = null,
    retryTempId = null,
  ) => {
    const imageFile = retryFile || pendingImage?.file;
    const localPreviewUrl = retryFile
      ? pendingImage?.previewUrl || ""
      : pendingImage?.previewUrl || "";
    if (!imageFile) return;

    const trimmedCaption = captionText?.trim() || null;
    const replyId = replyMessage ? replyMessage.id : null;

    // Generate tempId for this image send
    const tempId =
      retryTempId ||
      `opt_img_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Add optimistic image message immediately — show local preview
    if (!retryTempId) {
      dispatch({
        type: ACTIONS.ADD_OPTIMISTIC_MESSAGE,
        payload: {
          roomId,
          message: {
            id: tempId,
            tempId,
            message: localPreviewUrl,
            local_preview_url: localPreviewUrl,
            file_type: MESSAGE_FILE_TYPES.IMAGE,
            is_file: 1,
            caption: trimmedCaption,
            reply_to_id: replyId,
            sender_id: userData.id,
            room_id: roomId,
            is_optimistic: true,
            is_pending: true,
            is_failed: false,
            is_read: 0,
            created_at: new Date().toISOString(),
            // Store retry data so user can retry if send fails
            _retry_data: { file: imageFile, captionText: trimmedCaption },
          },
        },
      });
    }

    // Clear input state immediately so UI feels instant
    if (!retryFile) {
      setPendingImage(null);
      if (replyMessage) setReplyMessage(null);
    }
    setNewMessageCount(0);
    isAtBottomRef.current = true;
    setTimeout(() => scrollToExactBottom(true), 100);

    // Upload in background
    setUploadingImage(true);
    const result = await uploadMessageImageApiCall(imageFile);
    setUploadingImage(false);

    if (!result?.success) {
      dispatch({
        type: ACTIONS.FAIL_OPTIMISTIC_MESSAGE,
        payload: { roomId, tempId },
      });
      return;
    }

    // Pre-load the Cloudinary URL into the browser cache so the swap is invisible.
    // Once cached, update the optimistic message in-place (blob → remote URL),
    // then revoke the blob. The user sees one continuous image with no flash.
    await new Promise((resolve) => {
      const preloader = new window.Image();
      preloader.onload = resolve;
      preloader.onerror = resolve; // don't block on CDN error
      preloader.src = result.url;
    });
    dispatch({
      type: ACTIONS.UPDATE_OPTIMISTIC_IMAGE_SRC,
      payload: { roomId, tempId, message: result.url },
    });
    URL.revokeObjectURL(localPreviewUrl);

    // Emit socket with pre-created tempId so NEW_MESSAGE confirm removes the optimistic
    sendMessageToChat({
      roomId,
      message: result.url,
      receiverId,
      fileType: MESSAGE_FILE_TYPES.IMAGE,
      roomIsNew: !newChat,
      replyToId: replyId,
      caption: trimmedCaption,
      tempId, // signal AppContext to skip creating a duplicate optimistic
      existingTempId: tempId,
    });
  };

  const handleRetryMessage = (failedMessage) => {
    // Remove failed optimistic first
    dispatch({
      type: ACTIONS.CONFIRM_OPTIMISTIC_MESSAGE,
      payload: { roomId, tempId: failedMessage.id },
    });

    if (failedMessage.is_file && failedMessage._retry_data?.file) {
      // Image retry: re-run upload flow with stored file
      handleSendWithImage(
        failedMessage._retry_data.captionText || "",
        failedMessage._retry_data.file,
        `opt_img_retry_${Date.now()}`,
      );
    } else {
      // Text retry: re-emit socket (AppContext creates new optimistic automatically)
      sendMessageToChat({
        roomId,
        message: failedMessage.message,
        receiverId,
        fileType: failedMessage.file_type,
        roomIsNew: !newChat,
        replyToId: failedMessage.reply_to_id || null,
        caption: failedMessage.caption || null,
      });
    }
  };

  const handleSendMessage = () => {
    if (message.trim() !== "") {
      const messageDataToSend = {
        roomId,
        message,
        receiverId,
        fileType: MESSAGE_FILE_TYPES.TEXT,
        roomIsNew: !newChat,
        replyToId: replyMessage ? replyMessage.id : null,
      };
      sendMessageToChat(messageDataToSend);

      if (replyMessage) {
        setReplyMessage(null);
      }
      setMessage("");

      // Always scroll to bottom when YOU send — regardless of scroll position
      setNewMessageCount(0);
      isAtBottomRef.current = true;
      setTimeout(() => {
        scrollToExactBottom(true);
      }, 100);

      // Only apply countdown timer for group chats, not for direct messages
      if (
        activeChat.type === CHAT_ROOMS.TYPES.GROUP &&
        !isCurrentUserModerator
      ) {
        setCountdown(
          Number(userData.available_xp) >= activeChat.user_xp_level ? 15 : 30,
        );
      }
    }
  };

  const handleSendOrEdit = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (editedMessage) {
      const messageId = selectedMessage?.id;
      const dataToUpdate = { message: editedMessage };
      editChatMessageApiCall(roomId, messageId, dataToUpdate);
      setEditedMessage("");
      setSelectedMessage(null);
    } else if (pendingImage) {
      // Image (with optional caption) — send image+caption together
      const isDirectMessage = activeChat.type === CHAT_ROOMS.TYPES.SINGLE;
      if (isDirectMessage || isCurrentUserModerator || countdown === 0) {
        handleSendWithImage(message);
        setMessage("");
      }
    } else {
      // Plain text
      const isDirectMessage = activeChat.type === CHAT_ROOMS.TYPES.SINGLE;
      if (isDirectMessage || isCurrentUserModerator || countdown === 0) {
        handleSendMessage();
      }
    }
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      handleSendOrEdit(event);
    }
  };

  const getRepliedMsg = (messageId) => {
    if (!messageId || !activeChat?.messages) return null;
    const result = activeChat.messages.find(
      (message) => message?.id?.toString() === messageId?.toString(),
    );
    return result
      ? {
          id: result?.id,
          message: result?.message,
          is_file: result?.is_file,
          file_type: result?.file_type,
          caption: result?.caption || null,
          is_deleted: result?.is_deleted,
        }
      : null;
  };

  const cancelMessageEditing = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (editedMessage) {
      setEditedMessage("");
      setSelectedMessage(null);
    }
  };

  const handleEditMessage = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setAnchorEl(null);
    if (!selectedMessage) return;
    // Use direct computation — don't rely on potentially-cleared isEditableMessage state
    if (!isMessageEditable(selectedMessage)) return;
    setEditedMessage(selectedMessage.message);
    setIsEditableMessage(false);
  };

  const handleDeleteMessage = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!selectedMessage) return;
    const messageId = selectedMessage.id;
    await deleteChatMessageApiCall(roomId, messageId);
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  // Edit eligibility — priority order:
  // 1. is_editable_until — server-computed absolute UTC deadline; works after refresh, any device.
  // 2. local_sent_at    — client ms set at send time; valid for the current session.
  // 3. created_at       — UTC ISO from DB; always available as final fallback.
  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  const isMessageEditable = (msg) => {
    if (!msg) return false;

    // local_sent_at is set by the CLIENT clock (Date.now()) — always correct regardless
    // of server/client clock discrepancy. Use it as the primary source of truth.
    if (msg.local_sent_at) {
      return (Date.now() - Number(msg.local_sent_at)) < EDIT_WINDOW_MS;
    }

    // is_editable_until is computed server-side from created_at. If the server clock
    // is behind the client, correct by detecting the offset from local_sent_at vs created_at.
    if (msg.is_editable_until) {
      const deadline = Date.parse(msg.is_editable_until);
      if (!isNaN(deadline)) return Date.now() < deadline;
    }

    // Final fallback: compare against created_at directly.
    if (msg.created_at) {
      let s = String(msg.created_at).trim().replace(' ', 'T');
      if (!s.endsWith('Z') && !s.includes('+')) s += 'Z';
      return (Date.now() - Date.parse(s)) < EDIT_WINDOW_MS;
    }

    return false;
  };

  const handleReplyMessage = (message, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const isImageMsg =
      message.is_file && message.file_type === MESSAGE_FILE_TYPES.IMAGE;
    const truncatedMessage = isImageMsg
      ? message.message
      : TruncateString(message?.message || "", 100);
    setReplyMessage({
      id: message.id,
      message: truncatedMessage,
      sender: message.username || message.user?.username,
      isImage: isImageMsg,
      caption: message.caption || null,
    });

    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 100);
  };

  const handleScrollToRepliedMessage = (messageId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedRepliedMessageId(messageId);
  };

  const handleMsgInteraction = (type, messageId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    updateChatMessageInteractionApiCall(roomId, messageId, type);
  };

  const handleMsgTipping = (messageId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    updateChatMessageTipApiCall(roomId, messageId);
  };

  // Pre-compute which image messages are "recent" (last 5) for auto-download
  const last5ImageIndices = (() => {
    const imageIdxs = [];
    displayMessages.forEach((msg, idx) => {
      if (msg.is_file && msg.file_type === MESSAGE_FILE_TYPES.IMAGE)
        imageIdxs.push(idx);
    });
    return new Set(imageIdxs.slice(-5));
  })();

  const scrollToBottom = () => {
    if (isAtBottomRef.current) {
      setTimeout(() => scrollToExactBottom(true), 50);
    }
  };

  let previousDate = null;

  return (
    <MessageFieldContainer>
      {/* WhatsApp-style Search */}
      {showChatSearch && (
        <ChatSearch
          messages={displayMessages}
          onClose={handleCloseSearch}
          onResultSelect={handleSearchResultSelect}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          currentIndex={currentSearchIndex}
          onSearchResults={setSearchResults}
        />
      )}

      {/* Only show balance for group chats, not for direct messages */}
      {activeChat.type === CHAT_ROOMS.TYPES.GROUP && (
        <BalanceContainer>
          <BalanceItem title={userData?.available_coins?.toLocaleString()}>
            <b>Coins:</b>
            <BalanceValue>
              {FormatNumberCounter(userData?.available_coins || 0)}
            </BalanceValue>
          </BalanceItem>
          <BalanceItem title={userData?.available_xp?.toLocaleString()}>
            <b>XP:</b>
            <BalanceValue>
              {FormatNumberCounter(userData?.available_xp || 0)}
            </BalanceValue>
          </BalanceItem>
        </BalanceContainer>
      )}

      <MessagesContainer
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        style={{ fontSize: fontSizePx, ...wallpaperStyle }}
      >
        {isLoadingMoreMessages && (
          <LoadingIndicator>
            <div className="spinner" />
            <Typography
              variant="caption"
              sx={{
                ml: 1.5,
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.80)"
                    : "rgba(0,0,0,0.70)",
                fontWeight: 500,
              }}
            >
              Loading more messages...
            </Typography>
          </LoadingIndicator>
        )}

        {shouldShowOlderMessagesNotice && (
          <MessagesTopNotice>
            Only showing the last 100 messages
          </MessagesTopNotice>
        )}

        {!displayMessages?.length && (
          <EmptyState>
            <h2>Send messages across</h2>
          </EmptyState>
        )}

        {displayMessages.map((message, index) => {
          // Safety check for message structure
          if (!message || !message.id) {
            return null;
          }

          // User has dismissed this deleted/deleted-for-me placeholder
          if (
            (message.is_deleted || message.is_deleted_for_me) &&
            dismissedMessages.has(message.id.toString())
          ) {
            return null;
          }

          // Show avatar only on the last message of a consecutive block from the same sender
          const nextMessage = displayMessages[index + 1];
          const isLastInBlock =
            !nextMessage ||
            nextMessage.sender_id?.toString() !== message.sender_id?.toString();

          const messageSenderData = getParticipantData(
            activeChat.room_participants,
            message.sender_id,
          ) || { username: "Unknown", is_admin: 0, room_team_id: null };

          const messageSenderTeamId = messageSenderData?.room_team_id;
          const messageInteraction = message.interaction || {
            count: { like: 0, dislike: 0 },
            type: null,
          };
          const messageTippingCount = message.tip_count || 0;
          const truncatedUsername = TruncateString(
            messageSenderData?.username || "Unknown",
            20,
          );

          const getRepliedMessage = getRepliedMsg(message.reply_to_id);
          const truncatedRepliedMessage = getRepliedMessage
            ? TruncateString(getRepliedMessage.message, 100)
            : null;
          // Prefer local_sent_at (client clock) over created_at (server clock) for display —
          // avoids the 1-hour gap caused by server/client clock discrepancy.
          const displayTimestamp = message.local_sent_at
            ? new Date(Number(message.local_sent_at)).toISOString()
            : message.created_at;
          const formattedMessageDate = FormatDateDurationForMessagesDate(displayTimestamp);
          const formattedMessageTime = FormatDateDurationForMessagesTime(displayTimestamp);
          const isMyMessage =
            userData.id?.toString() === message.sender_id?.toString();

          const userTeamCode =
            messageSenderData?.is_admin === 1
              ? "admin"
              : getTeamCode(roomTeams, messageSenderTeamId);

          const likeInteractionCount = messageInteraction.count?.like || 0;
          const dislikeInteractionCount =
            messageInteraction.count?.dislike || 0;
          const myMsgInteractionType = messageInteraction.type ?? null;

          let dateComponent = null;
          if (formattedMessageDate !== previousDate) {
            dateComponent = (
              <DateDemarcation>{formattedMessageDate}</DateDemarcation>
            );
            previousDate = formattedMessageDate;
          }

          // System notice — render as centred pill; if it references a message (e.g. tip), make it clickable
          if (message.is_system) {
            const sysPill = message.reply_to_id ? (
              <span
                onClick={() =>
                  handleScrollToRepliedMessage(message.reply_to_id)
                }
                style={{
                  cursor: "pointer",
                  textDecoration: "underline dotted",
                }}
                title="Click to view referenced message"
              >
                {message.message}
              </span>
            ) : (
              <span>{message.message}</span>
            );
            return (
              <React.Fragment key={message.id}>
                {dateComponent}
                <SystemNotice>{sysPill}</SystemNotice>
              </React.Fragment>
            );
          }

          return (
            <MessageItem
              key={message.id}
              isReplied={
                message.id?.toString() === selectedRepliedMessageId?.toString()
              }
              isHighlighted={
                messageSearchResults.length > 0 &&
                currentSearchIndex > 0 &&
                messageSearchResults[currentSearchIndex - 1]?.messageId ===
                  message.id
              }
              ref={
                message.id?.toString() === selectedRepliedMessageId?.toString()
                  ? selectedRepliedMessageIdRef
                  : null
              }
              data-message-id={message.id}
              onCopy={(e) => {
                if (message.is_deleted) { e.preventDefault(); return; }
                // For file messages use caption; never copy a raw file URL.
                const raw = message.is_file ? (message.caption || null) : message.message;
                if (raw) {
                  e.preventDefault();
                  e.clipboardData.setData("text/plain", raw);
                }
              }}
            >
              {dateComponent}
              <MessageRow isSelf={isMyMessage}>
                {/* Sender avatar — only on the last message of a consecutive block */}
                {!isMyMessage &&
                  !message.is_deleted &&
                  (isLastInBlock ? (
                    <SenderAvatar
                      src={messageSenderData?.profile_image_url || undefined}
                      alt={messageSenderData?.username}
                    >
                      {messageSenderData?.username?.[0]?.toUpperCase() || "?"}
                    </SenderAvatar>
                  ) : (
                    <Box sx={{ width: 28, flexShrink: 0 }} />
                  ))}

                {/* Deleted / deleted-for-me placeholder */}
                {(message.is_deleted || message.is_deleted_for_me) ? (
                  <DeletedBubble isSelf={isMyMessage}>
                    <span style={{ fontSize: "16px", flexShrink: 0 }}>🚫</span>
                    <span style={{ flex: 1 }}>
                      {message.is_deleted_for_me ? "You deleted this message" : "This message was deleted"}
                    </span>
                    <IconButton
                      size="small"
                      title="Dismiss"
                      onClick={() => dismissDeletedMessage(message.id)}
                      sx={{ padding: "2px", opacity: 0.5, "&:hover": { opacity: 1 }, color: "inherit" }}
                    >
                      <CloseIcon sx={{ fontSize: "14px" }} />
                    </IconButton>
                  </DeletedBubble>
                ) : (
                  <SwipeableMessage
                    onReply={handleReplyMessage}
                    messageData={message}
                  >
                    <MessageBody isSelf={isMyMessage}>
                      {message.is_forwarded && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, opacity: 0.6 }}>
                          <ReplyAllIcon sx={{ fontSize: 14, transform: "scaleX(-1)" }} />
                          <Typography variant="caption" sx={{ fontSize: 11, fontStyle: "italic" }}>Forwarded</Typography>
                        </Box>
                      )}
                      {message.reply_to_id &&
                        (getRepliedMessage ? (
                          getRepliedMessage.is_deleted ? (
                            <ReplyPreview
                              style={{ fontStyle: "italic", opacity: 0.6 }}
                            >
                              🚫 Deleted message
                            </ReplyPreview>
                          ) : (
                            <ReplyPreview
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleScrollToRepliedMessage(
                                  getRepliedMessage.id,
                                  e,
                                );
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {getRepliedMessage.is_file &&
                              getRepliedMessage.file_type ===
                                MESSAGE_FILE_TYPES.IMAGE ? (
                                <>
                                  <img
                                    src={getRepliedMessage.message}
                                    alt="reply"
                                    style={{
                                      width: 48,
                                      height: 48,
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{ opacity: 0.8, fontSize: "12px" }}
                                  >
                                    {getRepliedMessage.caption || "Photo"}
                                  </span>
                                </>
                              ) : (
                                <>
                                  Replied:{" "}
                                  <span>{truncatedRepliedMessage}</span>
                                </>
                              )}
                            </ReplyPreview>
                          )
                        ) : (
                          <ReplyPreview
                            title="Refresh page to see hidden replied, if user is currently muted"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            Hidden or Deleted Reply
                          </ReplyPreview>
                        ))}

                      {message.is_file &&
                      message.file_type === MESSAGE_FILE_TYPES.IMAGE ? (
                        <ImageMessageBubble
                          src={message.local_preview_url || message.message}
                          caption={message.caption || null}
                          isRecent={last5ImageIndices.has(index)}
                          forceReveal={!!message.local_preview_url}
                          onLoaded={scrollToBottom}
                        />
                      ) : (
                        <MessageText>
                          {showChatSearch && searchQuery
                            ? highlightSearchTerm(message.message, searchQuery)
                            : parseMessageText(message.message)}
                        </MessageText>
                      )}

                      {/* Footer: tip (received) + timestamp + ticks + actions */}
                      <MessageFooter>
                        {!isMyMessage && (
                          <TipButton>
                            {messageTippingCount > 0 && (
                              <span>
                                {FormatNumberCounter(messageTippingCount)}
                              </span>
                            )}
                            <IconButton
                              size="small"
                              title="Tip"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMsgTipping(message.id, e);
                              }}
                            >
                              <CardGiftcardIcon />
                            </IconButton>
                          </TipButton>
                        )}
                        <TimeAndActions sx={{ ml: "auto" }}>
                          {message.is_blocked_period && (
                            <span
                              title="Sent during a block period — not delivered"
                              style={{
                                fontSize: 11,
                                opacity: 0.7,
                                lineHeight: 1,
                              }}
                            >
                              🚫
                            </span>
                          )}
                          {message.disappears_at && !message.is_kept && (
                            <AccessTimeIcon
                              sx={{
                                fontSize: 12,
                                opacity: 0.6,
                                color: "inherit",
                              }}
                              titleAccess="Disappearing message"
                            />
                          )}
                          {message.is_kept === 1 && (
                            <PushPinIcon
                              sx={{
                                fontSize: 12,
                                opacity: 0.6,
                                color: "#25D366",
                              }}
                              titleAccess="Kept"
                            />
                          )}
                          {message.is_edited && (
                            <span style={{ fontSize: "10px", opacity: 0.55, fontStyle: "italic" }}>
                              edited
                            </span>
                          )}
                          <span>{formattedMessageTime}</span>
                          {isMyMessage &&
                            (message.is_pending ? (
                              <AccessTimeIcon
                                sx={{
                                  fontSize: 14,
                                  opacity: 0.55,
                                  color: "inherit",
                                }}
                                titleAccess="Sending…"
                              />
                            ) : message.is_failed ? (
                              <IconButton
                                size="small"
                                title="Failed — tap to retry"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRetryMessage(message);
                                }}
                                sx={{ padding: "2px", color: "#ff4444" }}
                              >
                                <ReplayIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            ) : (
                              <TickIcon
                                isRead={message.is_read === 1}
                                isOnline={otherUserOnline}
                              >
                                {message.is_read === 1 || otherUserOnline ? (
                                  <DoneAllIcon />
                                ) : (
                                  <DoneIcon />
                                )}
                              </TickIcon>
                            ))}
                          <IconButton
                            size="small"
                            title="Reply"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleReplyMessage(message, e);
                            }}
                          >
                            <ReplyIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="More options"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleMenuOptionClick(e, message);
                            }}
                          >
                            <MenuIcon />
                          </IconButton>
                        </TimeAndActions>
                      </MessageFooter>
                      {/* WhatsApp-style emoji reaction bubbles */}
                      {(() => {
                        const byEmoji = message.interaction?.count?.byEmoji;
                        if (!byEmoji || Object.keys(byEmoji).length === 0) return null;
                        const myEmoji = message.interaction?.my_reaction?.emoji;
                        return (
                          <Box sx={{
                            display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.4,
                            justifyContent: isMyMessage ? "flex-end" : "flex-start",
                          }}>
                            {Object.entries(byEmoji).map(([em, count]) => (
                              <Box
                                key={em}
                                onClick={() => updateChatMessageInteractionApiCall(
                                  roomId, message.id,
                                  ["😢","👎"].includes(em) ? MESSAGE_INTERACTIONS_TYPES.DISLIKE : MESSAGE_INTERACTIONS_TYPES.LIKE,
                                  em
                                )}
                                sx={{
                                  display: "flex", alignItems: "center", gap: 0.25,
                                  fontSize: 11, px: 0.5, py: 0.15,
                                  borderRadius: "12px", cursor: "pointer",
                                  backgroundColor: myEmoji === em
                                    ? "rgba(255,107,53,0.25)"
                                    : (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)",
                                  border: myEmoji === em ? "1px solid #ff6b35" : "1px solid transparent",
                                  transition: "transform 0.1s",
                                  "&:hover": { transform: "scale(1.1)" },
                                }}
                              >
                                <span style={{ fontSize: 11 }}>{em}</span>
                                {count > 1 && <span style={{ fontSize: 10, fontWeight: 500 }}>{count}</span>}
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </MessageBody>
                  </SwipeableMessage>
                )}
              </MessageRow>
            </MessageItem>
          );
        })}
        {(localSystemMessages?.[activeChat?.id] || []).map((sys) => (
          <SystemNotice key={sys.id}>
            <span>{sys.text}</span>
          </SystemNotice>
        ))}
        {isOtherUserTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Message action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuOptionClose}
        elevation={0}
        PaperProps={{
          sx: {
            backgroundColor: (t) => t.palette.mode === "dark" ? "#1e1e2e" : "#ffffff",
            color: (t) => t.palette.mode === "dark" ? "#f0f0f0" : "#111111",
            borderRadius: "13px",
            boxShadow: (t) => t.palette.mode === "dark"
              ? "0 12px 40px rgba(0,0,0,0.6)"
              : "0 8px 32px rgba(0,0,0,0.18)",
            minWidth: 175,
            py: 0.25,
            overflow: "hidden",
          },
        }}
      >
        {/* Reply */}
        <MenuItem
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation();
            if (selectedMessage) handleReplyMessage(selectedMessage, e);
            handleMenuOptionClose();
          }}
          sx={{ fontSize: 13, py: 0.6, px: 2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.15)" : "rgba(255,107,53,0.08)" } }}
        >
          <ReplyIcon sx={{ mr: 1, fontSize: 17, color: "#ff6b35" }} /> Reply
        </MenuItem>

        {/* React — only on others' messages (can't react to your own) */}
        {selectedMessage?.sender_id?.toString() !== userData?.id?.toString() && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              setReactionPickerOpen(true);
              setAnchorEl(null);
            }}
            sx={{ fontSize: 13, py: 0.6, px: 2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.15)" : "rgba(255,107,53,0.08)" } }}
          >
            <ThumbUpIcon sx={{ mr: 1, fontSize: 17, color: "#ff6b35" }} /> React
          </MenuItem>
        )}

        {/* Forward */}
        <MenuItem
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation();
            setMessageToForward(selectedMessage);
            setForwardDialogOpen(true);
            handleMenuOptionClose();
          }}
          sx={{ fontSize: 13, py: 0.6, px: 2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.15)" : "rgba(255,107,53,0.08)" } }}
        >
          <ReplyAllIcon sx={{ mr: 1, fontSize: 17, color: "#ff6b35", transform: "scaleX(-1)" }} /> Forward
        </MenuItem>

        {/* Copy */}
        {(() => {
          const isImageOnly = !!(selectedMessage?.is_file && !selectedMessage?.caption);
          const copyText = selectedMessage?.is_file ? (selectedMessage?.caption || null) : selectedMessage?.message;
          return !isImageOnly ? (
            <MenuItem
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (!copyText) { handleMenuOptionClose(); return; }
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(copyText).catch(() => {
                    const el = document.createElement("textarea"); el.value = copyText;
                    document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
                  });
                } else {
                  const el = document.createElement("textarea"); el.value = copyText;
                  document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
                }
                handleMenuOptionClose();
              }}
              sx={{ fontSize: 13, py: 0.6, px: 2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.15)" : "rgba(255,107,53,0.08)" } }}
            >
              <ContentCopyIcon sx={{ mr: 1, fontSize: 17, color: "#ff6b35" }} /> Copy
            </MenuItem>
          ) : null;
        })()}

        {/* Edit — own messages within 15 min */}
        {isEditableMessage && (
          <MenuItem
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditMessage(e); }}
            sx={{ fontSize: 13, py: 0.6, px: 2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.15)" : "rgba(255,107,53,0.08)" } }}
          >
            <EditIcon sx={{ mr: 1, fontSize: 17, color: "#ff6b35" }} /> Edit
          </MenuItem>
        )}

        {/* Info */}
        <MenuItem
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation();
            setInfoDialogOpen(true);
            setAnchorEl(null);
          }}
          sx={{ fontSize: 13, py: 0.6, px: 2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.15)" : "rgba(255,107,53,0.08)" } }}
        >
          <DoneAllIcon sx={{ mr: 1, fontSize: 17, color: "#ff6b35" }} /> Info
        </MenuItem>

        {/* Divider */}
        <Box sx={{ my: 0.5, borderTop: (t) => `1px solid ${t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}` }} />

        {/* Delete — opens dialog with per-user options */}
        <MenuItem
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation();
            setDeleteDialogOpen(true);
            setAnchorEl(null);
          }}
          sx={{ fontSize: 13, py: 0.6, px: 2, color: "#ff4444", "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,68,68,0.18)" : "rgba(255,68,68,0.08)" } }}
        >
          <DeleteForeverIcon sx={{ mr: 1, fontSize: 17, color: "#ff4444" }} /> Delete
        </MenuItem>
      </Menu>

      {/* Reaction picker — compact floating pill */}
      <Dialog
        open={reactionPickerOpen}
        onClose={() => setReactionPickerOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "40px",
            backgroundColor: (t) => t.palette.mode === "dark" ? "#1e1e2e" : "#fff",
            px: 1.25, py: 0.75,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            m: 0,
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {[
            { emoji: "👍", type: MESSAGE_INTERACTIONS_TYPES.LIKE },
            { emoji: "❤️", type: MESSAGE_INTERACTIONS_TYPES.LIKE },
            { emoji: "😂", type: MESSAGE_INTERACTIONS_TYPES.LIKE },
            { emoji: "😮", type: MESSAGE_INTERACTIONS_TYPES.LIKE },
            { emoji: "😢", type: MESSAGE_INTERACTIONS_TYPES.DISLIKE },
            { emoji: "👎", type: MESSAGE_INTERACTIONS_TYPES.DISLIKE },
          ].map(({ emoji: e, type }) => {
            const myReaction = selectedMessage?.interaction?.my_reaction;
            const isSelected = myReaction?.emoji === e;
            return (
              <Box
                key={e}
                onClick={() => {
                  if (selectedMessage) updateChatMessageInteractionApiCall(roomId, selectedMessage.id, type, e);
                  setReactionPickerOpen(false);
                }}
                sx={{
                  fontSize: 22, lineHeight: 1, cursor: "pointer",
                  p: 0.5, borderRadius: "50%",
                  transition: "transform 0.12s, background 0.12s",
                  backgroundColor: isSelected ? "rgba(255,107,53,0.2)" : "transparent",
                  outline: isSelected ? "2px solid #ff6b35" : "none",
                  "&:hover": { transform: "scale(1.3)" },
                }}
              >
                {e}
              </Box>
            );
          })}
        </Box>
      </Dialog>

      {/* Message info dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: (t) => t.palette.mode === "dark" ? "#1e1e2e" : "#fff",
            minWidth: 280,
            boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, color: (t) => t.palette.mode === "dark" ? "#f0f0f0" : "#111", pb: 0.5 }}>
          Message Info
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (() => {
            let tStr = String(selectedMessage.created_at || '').trim().replace(' ', 'T');
            if (tStr && !tStr.endsWith('Z') && !tStr.includes('+')) tStr += 'Z';
            const sentDate = new Date(tStr);
            const sentAt = !isNaN(sentDate.getTime())
              ? sentDate.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
              : '—';
            const isOwn = selectedMessage.sender_id?.toString() === userData?.id?.toString();
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                {selectedMessage.message && (
                  <Box>
                    <Box sx={{ fontSize: 11, fontWeight: 600, color: "#ff6b35", mb: 0.25, textTransform: "uppercase", letterSpacing: 0.5 }}>Message</Box>
                    <Box sx={{ fontSize: 13, color: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.75)", wordBreak: "break-word" }}>
                      {selectedMessage.message}
                    </Box>
                  </Box>
                )}
                <Box>
                  <Box sx={{ fontSize: 11, fontWeight: 600, color: "#ff6b35", mb: 0.25, textTransform: "uppercase", letterSpacing: 0.5 }}>Sent</Box>
                  <Box sx={{ fontSize: 13, color: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.75)" }}>{sentAt}</Box>
                </Box>
                {isOwn && (
                  <Box>
                    <Box sx={{ fontSize: 11, fontWeight: 600, color: "#ff6b35", mb: 0.25, textTransform: "uppercase", letterSpacing: 0.5 }}>Status</Box>
                    <Box sx={{ fontSize: 13, color: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.75)" }}>
                      {selectedMessage.is_read === 1 ? "Read" : "Delivered"}
                    </Box>
                  </Box>
                )}
                {selectedMessage.is_edited && (
                  <Box>
                    <Box sx={{ fontSize: 11, fontWeight: 600, color: "#ff6b35", mb: 0.25, textTransform: "uppercase", letterSpacing: 0.5 }}>Edited</Box>
                    <Box sx={{ fontSize: 13, color: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.75)" }}>Yes</Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <Box sx={{ px: 2, pb: 1.5, display: "flex", justifyContent: "flex-end" }}>
          <Box
            onClick={() => setInfoDialogOpen(false)}
            sx={{ fontSize: 13, fontWeight: 600, color: "#ff6b35", cursor: "pointer", px: 1.5, py: 0.5, borderRadius: "8px", "&:hover": { backgroundColor: "rgba(255,107,53,0.1)" } }}
          >
            Close
          </Box>
        </Box>
      </Dialog>

      {/* Delete options dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: (t) => t.palette.mode === "dark" ? "#1e1e2e" : "#fff",
            minWidth: 260,
            boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, color: (t) => t.palette.mode === "dark" ? "#f0f0f0" : "#111", pb: 0.5 }}>
          Delete message?
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {/* Delete for everyone — own messages only */}
            {selectedMessage?.sender_id?.toString() === userData?.id?.toString() && (
              <Box
                onClick={(e) => {
                  handleDeleteMessage(e);
                  setDeleteDialogOpen(false);
                }}
                sx={{
                  fontSize: 14, py: 1.25, px: 1.5, borderRadius: "10px", cursor: "pointer", color: "#ff4444", fontWeight: 500,
                  "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,68,68,0.15)" : "rgba(255,68,68,0.08)" },
                }}
              >
                <DeleteForeverIcon sx={{ mr: 1, fontSize: 18, verticalAlign: "middle", color: "#ff4444" }} />
                Delete for everyone
              </Box>
            )}
            {/* Delete for me */}
            <Box
              onClick={() => {
                if (selectedMessage) {
                  deleteMessageForMeApiCall(roomId, selectedMessage.id);
                  dispatch({ type: ACTIONS.DELETE_MESSAGE_FOR_ME, payload: { roomId, messageId: selectedMessage.id } });
                }
                setDeleteDialogOpen(false);
              }}
              sx={{
                fontSize: 14, py: 1.25, px: 1.5, borderRadius: "10px", cursor: "pointer", color: "#ff8800", fontWeight: 500,
                "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,136,0,0.15)" : "rgba(255,136,0,0.08)" },
              }}
            >
              <VisibilityOffIcon sx={{ mr: 1, fontSize: 18, verticalAlign: "middle", color: "#ff8800" }} />
              Delete for me
            </Box>
          </Box>
        </DialogContent>
        <Box sx={{ px: 2, pb: 1.5, display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Box
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ fontSize: 13, fontWeight: 600, color: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)", cursor: "pointer", px: 1.5, py: 0.5, borderRadius: "8px", "&:hover": { backgroundColor: "rgba(128,128,128,0.1)" } }}
          >
            Cancel
          </Box>
        </Box>
      </Dialog>

      {/* Scroll to Bottom Button - Show when scrolled up OR when there are new messages */}
      {(showScrollButton || newMessageCount > 0) && !messageSearchQuery && (
        <ScrollToBottomButton
          onClick={() => {
            setNewMessageCount(0);
            isAtBottomRef.current = true;
            scrollToExactBottom(true);
          }}
          size="small"
        >
          <KeyboardArrowDownIcon />
          {newMessageCount > 0 && (
            <NewMessageBadge>
              {newMessageCount > 99 ? "99+" : newMessageCount}
            </NewMessageBadge>
          )}
        </ScrollToBottomButton>
      )}

      {replyMessage && (
        <ReplyContainer>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              overflow: "hidden",
            }}
          >
            <ReplyIcon
              sx={{ fontSize: 18, color: "primary.main", flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              {replyMessage.sender ? `${replyMessage.sender}: ` : "Replying: "}
            </Typography>
            {replyMessage.isImage ? (
              <>
                <img
                  src={replyMessage.message}
                  alt="reply preview"
                  style={{
                    width: 50,
                    height: 50,
                    objectFit: "cover",
                    borderRadius: "6px",
                    flexShrink: 0,
                  }}
                />
                {replyMessage.caption && (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? alpha("#fff", 0.8)
                          : alpha(theme.palette.text.primary, 0.75),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {replyMessage.caption}
                  </Typography>
                )}
              </>
            ) : (
              <Typography
                sx={{
                  fontSize: "13px",
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? alpha("#fff", 0.8)
                      : alpha(theme.palette.text.primary, 0.75),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {TruncateString(replyMessage.message, 150)}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setReplyMessage(null);
            }}
            title="Cancel Reply"
          >
            <CancelIcon
              sx={{
                fontSize: "20px",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#fff" : "rgba(0,0,0,0.6)",
              }}
            />
          </IconButton>
        </ReplyContainer>
      )}

      {/* Image+caption preview — shown when user picks an image before sending */}
      {pendingImage && (
        <Box
          sx={{
            padding: "10px 20px 0",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? alpha("#0a0a0a", 0.95)
                : "#fafafa",
            borderTop: (theme) =>
              `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.08) : alpha("#000", 0.08)}`,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <img
              src={pendingImage.previewUrl}
              alt="to send"
              style={{
                width: 72,
                height: 72,
                objectFit: "cover",
                borderRadius: "8px",
                display: "block",
              }}
            />
            <IconButton
              size="small"
              onClick={() => {
                URL.revokeObjectURL(pendingImage.previewUrl);
                setPendingImage(null);
              }}
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                width: 20,
                height: 20,
                padding: 0,
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                "&:hover": { background: "rgba(0,0,0,0.8)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: "12px", opacity: 0.6 }}>
            Add a caption below, then press Send
          </Typography>
        </Box>
      )}

      <InputContainer>
        {editedMessage ? (
          <textarea
            ref={messageInputRef}
            placeholder="Type a message..."
            value={editedMessage}
            onChange={(e) => setEditedMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <textarea
            ref={messageInputRef}
            placeholder="Type a message..."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (!roomId || !otherUser?.id) return;
              if (!isTyping) {
                setIsTyping(true);
                socket.emit(SOCKET_EVENTS.USER_TYPING, {
                  roomId,
                  receiverId: otherUser.id,
                  isTyping: true,
                });
              }
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => {
                setIsTyping(false);
                socket.emit(SOCKET_EVENTS.USER_TYPING, {
                  roomId,
                  receiverId: otherUser.id,
                  isTyping: false,
                });
              }, TYPING_EMIT_DELAY);
            }}
            onKeyDown={handleKeyDown}
            autoFocus={!editedMessage}
          />
        )}

        {/* Hidden image file input */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageSelect}
        />

        <ButtonGroup>
          {countdown > 0 &&
            !editedMessage &&
            activeChat.type === CHAT_ROOMS.TYPES.GROUP && (
              <Countdown>{countdown} secs</Countdown>
            )}

          {editedMessage && (
            <CancelButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                cancelMessageEditing(e);
              }}
            >
              Cancel
            </CancelButton>
          )}

          {!editedMessage && (
            <IconRow>
              {/* Image button */}
              <IconButton
                size="small"
                title="Send image"
                disabled={uploadingImage}
                onClick={() => imageInputRef.current?.click()}
                sx={{
                  color: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                  p: "6px",
                  "&:hover": { color: (theme) => theme.palette.primary.main },
                }}
              >
                {uploadingImage
                  ? <span style={{ width: 18, height: 18, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                  : <ImageIcon sx={{ fontSize: 22 }} />}
              </IconButton>

              {/* Emoji picker button — stays open until user closes it */}
              <IconButton
                ref={emojiButtonRef}
                size="small"
                title="Emoji"
                onClick={() => setEmojiOpen(o => !o)}
                sx={{
                  color: (theme) => emojiOpen
                    ? theme.palette.primary.main
                    : theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                  p: "6px",
                  "&:hover": { color: (theme) => theme.palette.primary.main },
                }}
              >
                <EmojiEmotionsIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </IconRow>
          )}

          {emojiOpen && (
            <EmojiPicker
              anchorRef={emojiButtonRef}
              onSelect={(emoji) => {
                if (!emoji) { setEmojiOpen(false); return; }
                const el = messageInputRef.current;
                const start = el ? (el.selectionStart ?? el.value.length) : null;
                const end = el ? (el.selectionEnd ?? el.value.length) : null;
                setMessage(prev => el
                  ? prev.slice(0, start) + emoji + prev.slice(end)
                  : prev + emoji
                );
                // Keep picker open so the user can select multiple emojis in one go.
                // Restore cursor to just after the inserted emoji.
                requestAnimationFrame(() => {
                  if (el) {
                    el.focus();
                    el.setSelectionRange(start + emoji.length, start + emoji.length);
                  }
                });
              }}
            />
          )}

          <SendButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSendOrEdit(e);
            }}
            disabled={
              editedMessage
                ? false
                : pendingImage
                  ? (activeChat.type === CHAT_ROOMS.TYPES.GROUP &&
                      !isCurrentUserModerator &&
                      countdown > 0) ||
                    uploadingImage
                  : (activeChat.type === CHAT_ROOMS.TYPES.GROUP &&
                      !isCurrentUserModerator &&
                      countdown > 0) ||
                    message.trim() === ""
            }
          >
            Send
          </SendButton>
        </ButtonGroup>
      </InputContainer>

      {/* Forward message dialog */}
      <Dialog
        open={forwardDialogOpen}
        onClose={() => { setForwardDialogOpen(false); setMessageToForward(null); setForwardSearch(""); }}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 320, maxWidth: 400, width: "100%" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Forward to…
          <IconButton size="small" onClick={() => { setForwardDialogOpen(false); setMessageToForward(null); setForwardSearch(""); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />

        {/* Search bar */}
        <Box sx={{ px: 2, py: 1, borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` }}>
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search chats…"
            value={forwardSearch}
            onChange={(e) => setForwardSearch(e.target.value)}
            startAdornment={<SearchIcon sx={{ mr: 1, opacity: 0.5, fontSize: 18 }} />}
            sx={{ fontSize: 14 }}
          />
        </Box>

        <DialogContent sx={{ p: 0, maxHeight: 380, overflowY: "auto" }}>
          {(() => {
            const filtered = (chats || []).filter(c => {
              if (!c.id || c.id === activeChat?.id) return false;
              if (!forwardSearch.trim()) return true;
              const other = c.room_participants?.find(p => p.id?.toString() !== userData?.id?.toString());
              const name = c.type === CHAT_ROOMS.TYPES.SINGLE ? (other?.username || c.name) : c.name;
              return name?.toLowerCase().includes(forwardSearch.toLowerCase());
            });

            if (filtered.length === 0) {
              return (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {forwardSearch.trim()
                      ? `No chats matching "${forwardSearch}"`
                      : "No other chats available"}
                  </Typography>
                </Box>
              );
            }

            return (
              <List disablePadding>
                {filtered.map((chat, idx) => {
                  const other = chat.room_participants?.find(
                    p => p.id?.toString() !== userData?.id?.toString()
                  );
                  const rawName = chat.type === CHAT_ROOMS.TYPES.SINGLE
                    ? (other?.username || chat.name)
                    : chat.name;
                  const displayName = capitalizeEachFirstWord(rawName || "");
                  const firstLetter = displayName?.[0]?.toUpperCase() || "?";
                  const avatarSrc = other?.profile_image_url || other?.avatar;

                  return (
                    <React.Fragment key={chat.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => {
                            if (!messageToForward) return;
                            const text = messageToForward.is_file
                              ? (messageToForward.caption || messageToForward.message || null)
                              : messageToForward.message;
                            if (text) {
                              sendMessageToChat({
                                message: text,
                                roomId: chat.id,
                                fileType: "text",
                                is_forwarded: true,
                              });
                            }
                            switchActiveChat(chat);
                            const targetUsername = other?.username;
                            history.push(targetUsername ? `/messages/${targetUsername}` : '/messages');
                            setForwardDialogOpen(false);
                            setMessageToForward(null);
                            setForwardSearch("");
                          }}
                          sx={{ px: 2, py: 1.2, "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,107,53,0.12)" : "rgba(255,107,53,0.07)" } }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={avatarSrc}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: avatarSrc ? "#ffffff" : "primary.main",
                                border: avatarSrc ? "1px solid rgba(0,0,0,0.08)" : "none",
                              }}
                            >
                              {!avatarSrc && firstLetter}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={displayName}
                            secondary={chat.is_archived ? "Archived" : null}
                            primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                            secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                          />
                        </ListItemButton>
                      </ListItem>
                      {idx < filtered.length - 1 && <Divider sx={{ ml: 8, opacity: 0.4 }} />}
                    </React.Fragment>
                  );
                })}
              </List>
            );
          })()}
        </DialogContent>
      </Dialog>
    </MessageFieldContainer>
  );
};

export default React.memo(MessageField);
